import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { assets } from '../../core/constants/assets.const';
import { SharedModule } from '../../shared/shared.module';
import { AuthService } from '../../../services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import {
  LoginResponse,
  logoutAlertResponse,
  logoutResponse
} from '../../models/auth.model';
import { SpinnerService } from '../../../services/spinner.service';
import { ToasterService } from '../../../services/toaster.service';
import { environment } from '../../../environments/environment';
import { responseStatus } from '../../core/constants/responseStatus.const';
import { DayDiffPipe } from '@esqyre/rental-lib';
import { resolveRouteHeader } from '../../core/helpers/route-header.helper';
import { SidebarContextService } from '../../../services/sidebar-context.service';
import { SocketService } from '../../../services/socket.service';
import { PointerDetails } from '../../models/sideBar';

@Component({
  selector: 'rental-main',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatMenuModule,
    CommonModule,
    RouterModule,
    SharedModule,
    DayDiffPipe,
    TranslateModule
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss'
})
export class MainComponent implements OnDestroy, OnInit {
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  protected subscriptionSubject$!: Subscription;
  protected assetsConst = assets;
  protected profile: LoginResponse | null = null;
  protected notifications: any[] = [];
  public sidenavWidth = 200;
  protected headingData: { heading: string; description: string } = {
    heading: '',
    description: ''
  };
  protected today = new Date();
  protected currentRoute: string = '';

  constructor(
    public _authService: AuthService,
    private router: Router,
    private _socketService: SocketService,
    private _spinnerService: SpinnerService,
    private _toasterService: ToasterService,
    private _translateService: TranslateService,
    private readonly sidebarContextService: SidebarContextService
  ) {
    this._socketService.initSocket();
    this.handlePointerListeners();
    this.getPointerSocketDetails();
  }

  handlePointerListeners(): void {
    this._socketService
      .onEvent('pointerSocket')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this._authService.socketPointerSubject.next(res.data);
        }
      });
  }

  getPointerSocketDetails(): void {
    this._spinnerService.addToLoader('getSocketDetails');
    this._authService
      .getPointerSocketDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: PointerDetails[]) => {
          this._spinnerService.removeFromLoader('getSocketDetails');
          this._authService.socketPointerSubject.next(res);
        },
        error: () => {
          this._spinnerService.removeFromLoader('getSocketDetails');
        }
      });
  }

  ngOnInit(): void {
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          this.profile = res;
        }
      });

    this.currentRoute = this.router.url;
    this.updateHeaderConfig(this.currentRoute);

    // Update on route change
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentRoute = event.urlAfterRedirects;
        this.updateHeaderConfig(this.currentRoute);
      });
  }

  handleRedirection(notification: any): void {
    switch (notification.pushType) {
    case 87:
      this.router.navigate(['/manage-approvals/0']);
      break;

    case 90: 
      this.router.navigate(['/manage-approvals/1']);
      break;
      
    default:
      this.router.navigate(['/received-notification']);
      break;
    }
  }

  private updateHeaderConfig(url: string): void {
    const contextUrl = this.sidebarContextService.resolveContextUrl(url);
    const headerConfig = resolveRouteHeader(url, contextUrl);

    if (headerConfig) {
      this.headingData = headerConfig;
    } else {
      this.headingData = { heading: '',
        description: '' };
    }
  }

  fetchPanelNotifications(): void {
    this._spinnerService.addToLoader('fetchPanelNotification');
    this._authService
      .fetchNotifications('')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this._spinnerService.removeFromLoader('fetchPanelNotification');
          this.notifications = response.notification;
        },
        error: () => {
          this._spinnerService.removeFromLoader('fetchPanelNotification');
        }
      });
  }

  logout(): void {
    this._authService.logoutAlert().then((res: logoutAlertResponse) => {
      if (
        Object.prototype.hasOwnProperty.call(res, 'value') &&
        res.isConfirmed === true
      ) {
        this._spinnerService.addToLoader('logout');
        this._authService
          .logout()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: logoutResponse) => {
              if (res.statusCode === responseStatus.SUCCESS) {
                sessionStorage.removeItem(environment.storageKey);
                this._spinnerService.removeFromLoader('logout');
                this.router.navigate(['/auth/login']);
                this._toasterService.successToastr(
                  this._translateService.instant('signOutSuccess')
                );
              }
            },
            error: () => {
              this._spinnerService.removeFromLoader('logout');
            }
          });
      }
    });
  }

  toggleSidenav(): void {
    this._authService.toggleSidebarView();
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
